// Crew compliance UI (George 2026-07-07): green/red/gray status per allocated
// employee, computed server-side against the job customer's site requirements
// (services/crewCompliance.js). Click a chip → modal with every check and its
// reason, pass or fail. Shared by the Operations Allocations drill-in, the
// Job Hub staffing section, Site Access and the employee record's "Cleared
// for" panel.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { styles, colors } from './UI';
import { EmployeeBadgesModal } from './EmployeeBadges';
// EmployeeRecordModal imports this file back (its "Cleared for" panel uses the
// chip and this modal). Both sides only touch the other's function
// declarations at render time, never at module evaluation, so the cycle is
// safe; keep it that way (no module-level use of either import).
import { EmployeeRecordModal } from './EmployeeRecordModal';
import { fixLinks, nextAction, modalMarkable, markRecordFromCheck } from '../utils/siteAccessView';
import { useAuth, isAdminLike } from '../context/AuthContext';
import { useHascStatus, hascVisible, isHascCheck, HascRegisterButton, HascRegisterPanel } from './HascRegistration';

// Fetch the whole job's compliance once; consumers look entries up by
// allocation_id (or employee_id) from the returned map.
export function useJobCompliance(projectId) {
  const [data, setData] = useState(null);
  // Bumped by consumers (e.g. the Job Hub facility picker) to re-evaluate
  // without remounting — changing the facility changes every chip's verdict.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!projectId) { setData(null); return; }
    let alive = true;
    api.jobCompliance(projectId)
      .then(d => { if (alive) setData(d); })
      .catch(() => { if (alive) setData(null); }); // never break the page over a status chip
    return () => { alive = false; };
  }, [projectId, tick]);
  if (data && typeof data === 'object' && !data.reload) data.reload = () => setTick(t => t + 1);
  return data;
}

const DOT = {
  green: { bg: '#D1FAE5', fg: '#065F46', dot: '#10B981', label: 'Site Ready' },
  amber: { bg: '#FEF3C7', fg: '#92400E', dot: '#F59E0B', label: 'Expiring' },
  red:   { bg: '#FEE2E2', fg: '#991B1B', dot: '#EF4444', label: 'Not Ready' },
  gray:  { bg: '#F3F4F6', fg: '#6B7280', dot: '#9CA3AF', label: 'No Rules' },
};

export function ComplianceChip({ entry, onClick }) {
  if (!entry) return <span style={{ color: '#D1D5DB', fontSize: 11 }}>…</span>;
  const s = DOT[entry.status] || DOT.gray;
  const fails = entry.checks.filter(c => c.status === 'fail').length;
  const warns = entry.checks.filter(c => c.status === 'warn').length;
  // A separated row or a placeholder seat is not a person being judged
  // (jobCompliance marker entries): say what it is, not "No Rules".
  const text = entry.separated ? 'Separated' : entry.open_seat ? 'Open seat'
    : entry.status === 'red' ? `${fails} issue${fails === 1 ? '' : 's'}`
    : entry.status === 'amber' ? `${warns} expiring` : s.label;
  return (
    <button
      type="button"
      onClick={onClick}
      title="Click for every check and reason"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
        background: s.bg, color: s.fg, border: 'none', borderRadius: 10,
        padding: '3px 10px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
      }}
    >
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.dot, flexShrink: 0 }} />
      {text}
    </button>
  );
}

const CHECK_ICON = { pass: '✅', fail: '❌', warn: '⚠️', na: '➖' };

// Fix-it destinations, keyed by utils/siteAccessView.js fixLinks: the badge
// modal opens right here; the rest open the Operations tab that owns the
// record, pre-searched to this employee (?emp=).
const FIX = {
  badges: { label: '🪪 Site badges', title: 'An active permanent site badge covers the drug-test age check (and nothing else).' },
  disa: { label: '🛡 DISA tab', tab: 'disa', title: 'Drug test, background, policies, random pool and the ISN number come from the DISA report.' },
  training: { label: '🎓 Training tab', tab: 'train', title: 'Record a class taken elsewhere, or correct a certificate number.' },
  isn: { label: '🌐 ISNetworld tab', tab: 'isn', title: 'Assign the class in ISNetworld, or see what the worker still owes there.' },
  roster: { label: '👥 Roster', tab: 'roster', title: 'TWIC expiry (📂 Docs) and the email ISNetworld needs live on the roster record.' },
};

// onChanged: called after something opened from here may have changed the
// verdict (a badge added), so the caller can re-evaluate. hideRecordLink:
// already inside the employee record.
export function ComplianceModal({ entry, job, onClose, onChanged, hideRecordLink = false }) {
  const navigate = useNavigate();
  const marker = !!(entry && (entry.separated || entry.open_seat));
  const empId = entry && !marker ? entry.employee_id : null;
  // Roster contact + identity for the header and the fix-it modals (GET
  // /employees/:id/records, the same allowOps read every caller sits behind).
  // A failed read only loses the contact line; the checks still render.
  const [person, setPerson] = useState(null);
  const [sub, setSub] = useState(null); // 'badges' | 'record' | null
  const [subError, setSubError] = useState('');
  // HASC Phase 2 (2026-09-23): "Register with HASC" on a failing or expiring
  // coded class. The status read happens only when such a check exists; the
  // button shows only for a configured account and a user the server allows.
  // A warn (Expiring) check gets the button only on a job's report: with no
  // job the server judges against today + 30, not this report's date.
  const withJob = !!(job && job.project_id);
  const hasc = useHascStatus(!!empId && (entry.checks || []).some(c => isHascCheck(c, withJob)));
  // `${label}#${index}` of the check whose confirm panel is open: the same
  // class listed on both Reqs cards gives two checks with one label, and one
  // click must open ONE billable confirm.
  const [hascFor, setHascFor] = useState(null);
  // A registration in flight: the modal stays open (no backdrop, Close, fix-it
  // or other Register click) until its result is on screen.
  const [hascBusy, setHascBusy] = useState(false);
  // Quick-mark one class taken outside our council feeds (2026-09-24). Same
  // gate and endpoint as the Site Access Quick-Mark grid: admin-like only
  // (POST /workforce/training/bulk is requireAdmin).
  const { user } = useAuth();
  const canMark = isAdminLike(user);
  const [markFor, setMarkFor] = useState(null); // `${label}#${index}`
  const [markDates, setMarkDates] = useState({ issued: '', expiry: '' });
  const [markSaving, setMarkSaving] = useState(false);
  const [markError, setMarkError] = useState('');
  useEffect(() => {
    setPerson(null); setSub(null); setSubError(''); setHascFor(null); setHascBusy(false);
    setMarkFor(null); setMarkError('');
    if (!empId) return undefined;
    let alive = true;
    api.employeeRecords(empId)
      .then(d => { if (alive) setPerson(d && d.employee ? d.employee : null); })
      .catch(() => { /* contact is a convenience */ });
    return () => { alive = false; };
  }, [empId]);
  if (!entry) return null;

  const emp = person || {
    id: entry.employee_id, full_name: entry.name, craft: entry.craft, employee_no: entry.employee_no,
    is_active: !(entry.inactive || entry.inactive_allocated),
  };
  // The badge / record modals replace this one while open (they sit on a
  // lower layer), and this one comes back when they close.
  if (sub === 'badges') {
    return <EmployeeBadgesModal employee={emp} onError={setSubError}
      onClose={() => { setSub(null); if (onChanged) onChanged(); }} />;
  }
  if (sub === 'record') {
    return <EmployeeRecordModal employee={emp} onError={setSubError} onClose={() => setSub(null)} />;
  }
  const go = (tab) => {
    onClose();
    navigate(`/operations?tab=${tab}&emp=${encodeURIComponent(emp.full_name || entry.name || '')}`);
  };
  const fixes = marker ? [] : fixLinks(entry);
  const dup = entry.possible_duplicate_of;
  const inactive = entry.inactive || entry.inactive_allocated;
  const docs = entry.badging_docs || [];

  const s = DOT[entry.status] || DOT.gray;
  const fails = entry.checks.filter(c => c.status === 'fail');
  const warns = entry.checks.filter(c => c.status === 'warn');
  const fmtD = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;
  const fixBtn = { ...styles.btnSecondary, fontSize: 11, padding: '4px 10px', minWidth: 0 };
  const openMark = (key) => { setMarkFor(key); setMarkDates({ issued: '', expiry: '' }); setMarkError(''); };
  // Saves, then closes: Site Access hands this modal a snapshot of the row,
  // so the refreshed verdict shows on the chip, not in here.
  const saveMark = async (c) => {
    setMarkSaving(true); setMarkError('');
    try {
      await api.bulkCreateTraining([{
        employee_id: empId, ...markRecordFromCheck(c),
        issued_date: markDates.issued || null, expiry_date: markDates.expiry || null,
        issuer: 'Manual (Site Ready quick-mark)',
      }]);
      if (onChanged) onChanged();
      onClose();
    } catch (err) {
      setMarkError(err.message || 'Save failed.');
    } finally {
      setMarkSaving(false);
    }
  };
  return (
    <div onClick={hascBusy ? undefined : onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1100,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 10, width: 560, maxWidth: '96vw',
        maxHeight: '88vh', overflowY: 'auto', padding: 18,
        boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
          <div style={{ minWidth: 0 }}>
            <h2 style={{ ...styles.h2, margin: 0 }}>{entry.name}</h2>
            <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 2 }}>
              {entry.craft}{entry.shift ? ` · ${entry.shift}` : ''}
              {job?.customer?.name ? <> · vs <strong>{job.customer.name}</strong>{job.customer.facility ? ' (facility)' : ''} requirements</> : ''}
              {job?.requirements_source ? ` (${job.requirements_source})` : ''}
            </div>
            {(entry.first_day_onsite || entry.compliance_through) && (
              <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 2 }}>
                {entry.first_day_onsite ? <>Onsite <strong>{fmtD(entry.first_day_onsite)}</strong>{entry.last_day_onsite ? <> → <strong>{fmtD(entry.last_day_onsite)}</strong></> : ''}</> : ''}
                {entry.compliance_through ? <> · verified through {fmtD(entry.compliance_through)} ({entry.first_day_onsite ? 'later of last day / job end, + 30' : 'ready-through date + 30'})</> : ''}
              </div>
            )}
            {/* Contact straight off the roster, so a gap can be chased with
                one call instead of a tab switch. */}
            {!marker && person && (
              <div style={{ fontSize: 12, marginTop: 4 }}>
                {person.mobile || person.phone
                  ? <a href={`tel:${person.mobile || person.phone}`}>📞 {person.mobile || person.phone}</a>
                  : <span style={{ color: '#B45309' }}>📞 no phone on the roster</span>}
                {' · '}
                {person.email
                  ? <a href={`mailto:${person.email}`}>✉ {person.email}</a>
                  : <span style={{ color: '#B45309' }}>✉ no email on the roster</span>}
              </div>
            )}
            {inactive && !marker && (
              <div style={{ fontSize: 12, color: '#B45309', fontWeight: 700, marginTop: 4 }}>
                Inactive on the roster, still allocated: evaluated here, but reactivate or remove him before he goes.
              </div>
            )}
            {dup && dup.name && (
              <div style={{ fontSize: 12, marginTop: 4 }}>
                <span style={{ background: '#EDE9FE', color: '#5B21B6', borderRadius: 10, padding: '2px 8px', fontWeight: 700 }}
                  title="Same surname and a first-name variant of another active roster row, and this row has no DISA or training record of its own. Check the other row before chasing this red. Nothing is ever merged automatically.">
                  Possible CRM duplicate of {dup.name}
                </span>
              </div>
            )}
          </div>
          <span style={{
            background: s.bg, color: s.fg, borderRadius: 10, padding: '4px 12px',
            fontSize: 12, fontWeight: 800, whiteSpace: 'nowrap', flexShrink: 0,
          }}>
            {entry.separated ? '● SEPARATED: HISTORY, NOT CREW'
              : entry.open_seat ? '● OPEN SEAT'
              : entry.status === 'red' ? `● NOT READY — ${fails.length} issue${fails.length === 1 ? '' : 's'}`
              : entry.status === 'amber' ? `● EXPIRES DURING ${entry.first_day_onsite ? 'JOB' : 'WINDOW'} — ${warns.length} item${warns.length === 1 ? '' : 's'}`
              : entry.status === 'green' ? '● SITE READY' : '● NO RULES ON FILE'}
          </span>
        </div>

        {/* Fix-it links by what is failing (utils/siteAccessView.js fixLinks). */}
        {!marker && (fixes.length > 0 || !hideRecordLink) && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
            {fixes.map(k => (
              <button key={k} type="button" style={fixBtn} title={FIX[k].title} disabled={hascBusy}
                onClick={() => (k === 'badges' ? setSub('badges') : go(FIX[k].tab))}>
                {FIX[k].label}
              </button>
            ))}
            {!hideRecordLink && (
              <button type="button" style={fixBtn} disabled={hascBusy} onClick={() => setSub('record')}
                title="DISA, training, ISN and every customer this employee is cleared for">
                🗂 Employee record
              </button>
            )}
          </div>
        )}
        {subError && <div style={{ ...styles.errorBox, marginTop: 8, marginBottom: 0 }}>{subError}</div>}

        <div style={{ marginTop: 12 }}>
          {entry.checks.map((c, i) => {
            const action = nextAction(c);
            const canHasc = !marker && hascVisible(hasc) && isHascCheck(c, withJob);
            const hascKey = `${c.label}#${i}`;
            return (
            <div key={i}>
            <div style={{
              display: 'flex', gap: 10, padding: '8px 10px', borderRadius: 6, marginBottom: 4,
              background: c.status === 'fail' ? '#FEF2F2' : c.status === 'warn' ? '#FFFBEB' : c.status === 'pass' ? '#F0FDF4' : '#F9FAFB',
              border: `1px solid ${c.status === 'fail' ? '#FECACA' : c.status === 'warn' ? '#FDE68A' : c.status === 'pass' ? '#BBF7D0' : '#E5E7EB'}`,
            }}>
              <span style={{ flexShrink: 0 }}>{CHECK_ICON[c.status] || '➖'}</span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1F2937' }}>
                  {c.label}
                  {action && (
                    <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, color: '#374151', background: '#E5E7EB', borderRadius: 8, padding: '1px 7px' }}
                      title="Next action">{action.label}</span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: '#374151', marginTop: 1 }}>{c.detail}</div>
                {canHasc && hascFor !== hascKey && <HascRegisterButton status={hasc} disabled={hascBusy} onClick={() => setHascFor(hascKey)} />}
                {canMark && !marker && modalMarkable(c, entry) && markFor !== hascKey && (
                  <button type="button" disabled={hascBusy || markSaving} onClick={() => openMark(hascKey)}
                    title="Taken with another employer or council we don't pull: record it here. Add-only; delete it on Operations → Training if wrong."
                    style={{ ...fixBtn, marginTop: 6, marginLeft: canHasc && hascFor !== hascKey ? 6 : 0 }}>
                    ✓ Taken elsewhere: mark on file
                  </button>
                )}
                {canMark && markFor === hascKey && (
                  <div style={{ marginTop: 6, padding: 8, background: '#fff', border: '1px solid #E5E7EB', borderRadius: 6 }}>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                      <label style={{ fontSize: 11, color: '#6B7280' }}>Completed
                        <input type="date" min="2000-01-01" max="2100-12-31" style={{ ...styles.input, display: 'block' }}
                          value={markDates.issued} onChange={e => setMarkDates(d => ({ ...d, issued: e.target.value }))} />
                      </label>
                      <label style={{ fontSize: 11, color: '#6B7280' }}>Expires
                        <input type="date" min="2000-01-01" max="2100-12-31" style={{ ...styles.input, display: 'block' }}
                          value={markDates.expiry} onChange={e => setMarkDates(d => ({ ...d, expiry: e.target.value }))} />
                      </label>
                      <button type="button" style={{ ...styles.btn, fontSize: 12, padding: '6px 12px' }}
                        disabled={markSaving} onClick={() => saveMark(c)}>{markSaving ? 'Saving…' : 'Save'}</button>
                      <button type="button" style={fixBtn} disabled={markSaving} onClick={() => setMarkFor(null)}>Cancel</button>
                    </div>
                    <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>
                      Records {markRecordFromCheck(c).certificate_no || 'the class'} as on file for {entry.name}. Set Expires from the card; blank = never lapses.
                    </div>
                    {markError && <div style={{ ...styles.errorBox, marginTop: 6, marginBottom: 0 }}>{markError}</div>}
                  </div>
                )}
              </div>
            </div>
            {canHasc && hascFor === hascKey && (
              <HascRegisterPanel key={`${empId}|${hascKey}`} employeeId={empId} employeeName={entry.name}
                courseCode={c.course_code} checkLabel={c.label} projectId={job?.project_id || null}
                onClose={() => setHascFor(null)} onRegistered={onChanged} onBusy={setHascBusy} onChanged={onChanged} />
            )}
            </div>
            );
          })}
          {job?.isn && (
            <div style={{
              display: 'flex', gap: 10, padding: '8px 10px', borderRadius: 6, marginTop: 8,
              background: job.isn.status === 'fail' ? '#FEF2F2' : '#EFF6FF',
              border: `1px solid ${job.isn.status === 'fail' ? '#FECACA' : '#93C5FD'}`,
            }}>
              <span style={{ flexShrink: 0 }}>🌐</span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700 }}>ISNetworld (job-level{job.isn.client_name ? ` · ${job.isn.client_name}` : ''})</div>
                {job.isn.status === 'fail' && job.isn.label && (
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#B91C1C', marginTop: 1 }}>{job.isn.label}</div>
                )}
                <div style={{ fontSize: 12, color: '#374151', marginTop: 1 }}>{job.isn.detail}</div>
              </div>
            </div>
          )}
        </div>

        {/* Decision 5 (2026-09-23): the customer's packet documents other than
            TWIC are a checklist for the badging office, never a color. */}
        {docs.length > 0 && (
          <div style={{ marginTop: 12, padding: '8px 10px', borderRadius: 6, background: '#F9FAFB', border: '1px solid #E5E7EB' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 4 }}>
              Badging packet documents (not part of the verdict)
            </div>
            {docs.map(d => (
              <div key={d.doc} style={{ fontSize: 12, color: d.on_file ? '#374151' : '#6B7280', marginBottom: 2 }}>
                {d.on_file ? '📄 On file' : '○ Not on file'} · {d.doc}
              </div>
            ))}
            <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>
              What the customer wants in the packet. Only TWIC is checked for Site Ready; upload the rest under Roster → 📂 Docs.
            </div>
          </div>
        )}

        <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 10 }}>
          Computed from DISA imports, training records, badging profiles, and the customer's
          site requirements — fix the record where it lives (the links above) to change a result.
          This is separate from the manual verification checkboxes.
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
          <button style={styles.btnSecondary} disabled={hascBusy} onClick={onClose}
            title={hascBusy ? 'Registering with HASC: wait for the result.' : undefined}>Close</button>
        </div>
      </div>
    </div>
  );
}
